/**
 * Admin: Duplicate player detection & merge (Timing DB)
 * -----------------------------------------------------
 * Gated behind ADMIN_EMAILS (backend) + `user.is_admin` (frontend UX).
 * Non-admins see a sign-in prompt.
 *
 * Flow:
 *   1. Backend returns candidate pairs (same team + non-overlapping seasons + fuzzy name)
 *   2. Admin reviews each pair side-by-side
 *   3. Choose canonical (winner) key OR reject the pair
 *   4. Merge = re-key GW rows + union teams/seasons + delete loser
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    timingListDuplicates,
    timingMergeDuplicate,
    timingRejectDuplicate,
    timingListAliases,
} from "../lib/timing-api";
import { useAuth } from "../contexts/AuthContext";

// ---------------------------------------------------------------------------
// Compact "player card" used inside a merge pair.
// ---------------------------------------------------------------------------
function PlayerCard({ p, isCanonical, onPick, disabled }) {
    return (
        <button
            type="button"
            onClick={onPick}
            disabled={disabled}
            data-testid={`dup-pick-${p.player_key}`}
            className={`text-left w-full border p-3 space-y-2 transition-colors ${
                isCanonical
                    ? "border-fa-mint bg-fa-mint/[0.06]"
                    : "border-white/15 bg-black/40 hover:bg-white/[0.03] hover:border-white/30"
            } disabled:opacity-40 disabled:cursor-not-allowed`}
        >
            <div className="flex items-center gap-2">
                <div className={`font-mono text-[9px] uppercase tracking-widest px-1 py-0.5 border ${isCanonical ? "border-fa-mint text-fa-mint" : "border-white/25 text-white/50"}`}>
                    {isCanonical ? "Zvoleno ✓" : "Zvolit jako správné"}
                </div>
                {p.positions?.length > 0 && (
                    <span className="font-mono text-[9px] text-white/40">{p.positions.join(" · ")}</span>
                )}
                {p.latest_price > 0 && (
                    <span className="font-mono text-[9px] tabular-nums text-fa-yellow ml-auto">
                        £{(p.latest_price / 10).toFixed(1)}m
                    </span>
                )}
            </div>
            <div className="font-display font-black text-base leading-tight">
                {p.display_name}
            </div>
            <div className="font-mono text-[10px] text-white/50 tabular-nums">
                key: <span className="text-white/80">{p.player_key}</span>
            </div>
            <div className="font-mono text-[10px] text-white/60 space-y-0.5">
                {p.seasons.map((s) => (
                    <div key={s} className="flex justify-between">
                        <span>{s}</span>
                        <span className="text-white/80">{p.teams?.[s] || "—"}</span>
                    </div>
                ))}
            </div>
        </button>
    );
}

// ---------------------------------------------------------------------------
// Single row per candidate pair.
// ---------------------------------------------------------------------------
function DuplicatePairRow({ pair, onMerge, onReject, busy }) {
    const [chosen, setChosen] = useState(pair.suggested_canonical);
    const canonical = chosen === pair.a.player_key ? pair.a : pair.b;
    const loser = chosen === pair.a.player_key ? pair.b : pair.a;

    return (
        <div
            className="border border-white/10 bg-[#0d0d0d] p-4 space-y-3"
            data-testid={`dup-pair-${pair.pair_id}`}
        >
            <div className="flex items-center gap-3 flex-wrap">
                <div className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 border ${
                    pair.score >= 90 ? "bg-fa-mint text-black border-fa-mint" :
                    pair.score >= 75 ? "bg-fa-yellow text-black border-fa-yellow" :
                    "border-white/25 text-white/60"
                }`}>
                    Shoda {pair.score}%
                </div>
                <div className="font-mono text-[10px] text-white/50">
                    Společné týmy: <span className="text-white/85">{pair.shared_teams.join(", ")}</span>
                </div>
                <div className="ml-auto flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => onReject(pair)}
                        disabled={busy}
                        data-testid={`dup-reject-${pair.pair_id}`}
                        className="font-mono text-[10px] uppercase tracking-widest border border-white/20 text-white/60 hover:text-red-200 hover:border-red-400/40 px-3 py-1.5 disabled:opacity-40"
                        title="Nejsou to duplicity — schovat tuto dvojici"
                    >
                        Zamítnout
                    </button>
                    <button
                        type="button"
                        onClick={() => onMerge(pair, chosen)}
                        disabled={busy}
                        data-testid={`dup-merge-${pair.pair_id}`}
                        className="font-mono text-[10px] uppercase tracking-widest bg-fa-mint text-black hover:bg-fa-mint/85 font-bold px-3 py-1.5 disabled:opacity-40"
                    >
                        Sloučit → {canonical.display_name}
                    </button>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <PlayerCard
                    p={pair.a}
                    isCanonical={chosen === pair.a.player_key}
                    onPick={() => setChosen(pair.a.player_key)}
                    disabled={busy}
                />
                <PlayerCard
                    p={pair.b}
                    isCanonical={chosen === pair.b.player_key}
                    onPick={() => setChosen(pair.b.player_key)}
                    disabled={busy}
                />
            </div>
            <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                Po sloučení: <span className="text-white/70">{loser.player_key}</span> → <span className="text-fa-mint">{canonical.player_key}</span>{" "}
                (přesune {loser.seasons.length} sezón: {loser.seasons.join(", ")})
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------
export default function AdminDuplicates() {
    const { user, loading: authLoading, login } = useAuth();
    const [pairs, setPairs] = useState([]);
    const [aliases, setAliases] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [showAliases, setShowAliases] = useState(false);

    const isAdmin = !!user?.is_admin;

    const load = useCallback(async () => {
        if (!isAdmin) return;
        setLoading(true);
        setError(null);
        try {
            const [dupData, aliasData] = await Promise.all([
                timingListDuplicates(),
                timingListAliases().catch(() => ({ aliases: [] })),
            ]);
            setPairs(dupData.pairs || []);
            setAliases(aliasData.aliases || []);
        } catch (e) {
            const detail = e?.response?.data?.detail || e?.message || "Načtení selhalo";
            setError(detail);
            toast.error(detail);
        } finally {
            setLoading(false);
        }
    }, [isAdmin]);

    useEffect(() => { load(); }, [load]);

    const doMerge = async (pair, canonicalKey) => {
        if (!window.confirm(`Sloučit ${pair.a.display_name} + ${pair.b.display_name}?\n\nVýsledek: ${canonicalKey}\nGW řádky ztraceného hráče budou přeneseny.`)) return;
        setBusy(true);
        try {
            const r = await timingMergeDuplicate({
                a_key: pair.a.player_key,
                b_key: pair.b.player_key,
                canonical_key: canonicalKey,
            });
            toast.success(`Sloučeno — přeneseno ${r.gw_rows_moved} GW řádků`);
            setPairs((cur) => cur.filter((p) => p.pair_id !== pair.pair_id));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Sloučení selhalo");
        } finally {
            setBusy(false);
        }
    };

    const doReject = async (pair) => {
        setBusy(true);
        try {
            await timingRejectDuplicate({ a_key: pair.a.player_key, b_key: pair.b.player_key });
            toast.success("Dvojice skryta");
            setPairs((cur) => cur.filter((p) => p.pair_id !== pair.pair_id));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Skrytí selhalo");
        } finally {
            setBusy(false);
        }
    };

    // Group pairs by score band for easier review.
    const grouped = useMemo(() => {
        const high = pairs.filter((p) => p.score >= 90);
        const medium = pairs.filter((p) => p.score >= 75 && p.score < 90);
        const low = pairs.filter((p) => p.score < 75);
        return { high, medium, low };
    }, [pairs]);

    if (authLoading) {
        return <div className="max-w-6xl mx-auto p-8 font-mono text-white/50">Načítám…</div>;
    }

    if (!user) {
        return (
            <div className="max-w-3xl mx-auto p-8 space-y-4">
                <h1 className="font-display font-black text-3xl">Admin — Duplicity hráčů</h1>
                <p className="text-white/60">Tato stránka je jen pro adminy. Přihlas se prosím.</p>
                <button
                    onClick={login}
                    data-testid="admin-signin"
                    className="font-mono text-[11px] uppercase tracking-widest bg-fa-yellow text-black px-4 py-2 hover:bg-fa-yellowHover font-bold"
                >
                    Přihlásit se
                </button>
            </div>
        );
    }

    if (!isAdmin) {
        return (
            <div className="max-w-3xl mx-auto p-8 space-y-2">
                <h1 className="font-display font-black text-3xl">Admin — Duplicity hráčů</h1>
                <p className="text-red-200">Účet <strong>{user.email}</strong> nemá admin oprávnění.</p>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6" data-testid="admin-duplicates-page">
            <header className="space-y-2">
                <h1 className="font-display font-black text-3xl md:text-4xl">Duplicity hráčů</h1>
                <p className="font-mono text-[11px] uppercase tracking-widest text-white/50">
                    Kandidáti spojení hráčů napříč historickou databází Fantasy Academy
                </p>
                <div className="flex items-center gap-3 flex-wrap font-mono text-[11px]">
                    <span className="text-white/50">Přihlášen jako <span className="text-white/85">{user.email}</span></span>
                    <button
                        type="button"
                        onClick={load}
                        disabled={loading || busy}
                        className="border border-white/20 px-3 py-1.5 hover:border-white/40 text-white/70 hover:text-white uppercase tracking-widest text-[10px] disabled:opacity-40"
                    >
                        {loading ? "Načítám…" : "Načíst znovu"}
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowAliases((v) => !v)}
                        className="border border-white/20 px-3 py-1.5 hover:border-white/40 text-white/70 hover:text-white uppercase tracking-widest text-[10px]"
                    >
                        {showAliases ? "Skrýt aliasy" : `Zobrazit aliasy (${aliases.length})`}
                    </button>
                </div>
            </header>

            {error && (
                <div className="border border-red-400/40 bg-red-500/10 p-3 font-mono text-[11px] text-red-200">
                    {error}
                </div>
            )}

            {showAliases && aliases.length > 0 && (
                <section className="border border-white/10 bg-[#0d0d0d] p-4 space-y-2">
                    <h3 className="font-display font-black text-sm uppercase tracking-widest">
                        Provedené aliasy ({aliases.length})
                    </h3>
                    <div className="space-y-1 font-mono text-[11px] max-h-60 overflow-y-auto">
                        {aliases.map((a) => (
                            <div key={a.alias_slug} className="flex justify-between border-b border-white/[0.05] py-1">
                                <span className="text-white/60">{a.alias_slug}</span>
                                <span className="text-fa-mint">→ {a.canonical_slug}</span>
                                {a.created_by && <span className="text-white/40 text-[9px]">{a.created_by}</span>}
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {loading && (
                <div className="font-mono text-[11px] text-white/50 italic">Skenuji databázi hráčů…</div>
            )}

            {!loading && pairs.length === 0 && !error && (
                <div className="border border-white/10 bg-[#0d0d0d] p-8 text-center space-y-2">
                    <div className="font-display font-black text-lg">Žádné duplicity nenalezeny 🎉</div>
                    <div className="font-mono text-[11px] text-white/50">
                        Databáze je čistá, nebo jsi všechny předchozí kandidáty vyřešil.
                    </div>
                </div>
            )}

            {!loading && pairs.length > 0 && (
                <>
                    <div className="font-mono text-[11px] uppercase tracking-widest text-white/50">
                        Nalezeno <span className="text-fa-yellow">{pairs.length}</span> kandidátů
                        · Vysoká shoda: <span className="text-fa-mint">{grouped.high.length}</span>
                        · Střední: <span className="text-fa-yellow">{grouped.medium.length}</span>
                        · Nízká: <span className="text-white/70">{grouped.low.length}</span>
                    </div>

                    {grouped.high.length > 0 && (
                        <section className="space-y-3">
                            <h2 className="font-display font-black text-lg text-fa-mint">
                                ↑ Vysoká shoda ({grouped.high.length}) — téměř jistě duplicity
                            </h2>
                            {grouped.high.map((p) => (
                                <DuplicatePairRow key={p.pair_id} pair={p} onMerge={doMerge} onReject={doReject} busy={busy} />
                            ))}
                        </section>
                    )}
                    {grouped.medium.length > 0 && (
                        <section className="space-y-3">
                            <h2 className="font-display font-black text-lg text-fa-yellow">
                                Střední shoda ({grouped.medium.length}) — zkontroluj pečlivě
                            </h2>
                            {grouped.medium.map((p) => (
                                <DuplicatePairRow key={p.pair_id} pair={p} onMerge={doMerge} onReject={doReject} busy={busy} />
                            ))}
                        </section>
                    )}
                    {grouped.low.length > 0 && (
                        <section className="space-y-3">
                            <h2 className="font-display font-black text-lg text-white/70">
                                ↓ Nízká shoda ({grouped.low.length}) — hodně false positive
                            </h2>
                            {grouped.low.map((p) => (
                                <DuplicatePairRow key={p.pair_id} pair={p} onMerge={doMerge} onReject={doReject} busy={busy} />
                            ))}
                        </section>
                    )}
                </>
            )}
        </div>
    );
}
